import { useId, useRef, type ReactNode } from 'react';
import { VisuallyHidden } from './VisuallyHidden.js';
import './Tabs.css';

export interface TabItem {
  id: string;
  label: ReactNode;
  /** Optional trailing count, rendered in mono tabular figures. */
  count?: number;
  /** Accessible name override — needed when `label` is not plain text. */
  accessibleLabel?: string;
  /**
   * A leading status dot, in the review-queue form. Decorative: the tone repeats
   * something the label already says, so it is `aria-hidden` and never the only
   * signal (a11y.md §2).
   */
  dot?: 'success' | 'warning' | 'danger' | 'accent' | 'neutral';
}

export interface TabsProps {
  items: TabItem[];
  /** The selected tab's id. Controlled — the caller owns the state. */
  value: string;
  onChange: (id: string) => void;
  /**
   * What the group is for, e.g. "Filter findings". Required: a bare row of
   * buttons announces as five unrelated controls, and a screen-reader user
   * arriving mid-page has no way to tell it is one filter.
   */
  label: string;
  /**
   * `tablist` (default) when these switch between panels of content, `group`
   * when they filter a list that stays in place.
   *
   * This is a real semantic distinction, not a preference: the `tablist` role
   * commits to `tabpanel`s existing and to arrow-key navigation *replacing* Tab,
   * which is wrong for a filter row where the list below is not a panel. Getting
   * it wrong makes a screen reader promise a structure that is not there.
   */
  as?: 'tablist' | 'group';
  /**
   * Visual form. The prototype has three, and they are not interchangeable:
   *
   *   `pill`  (default) bordered segmented pills — a filter row inside a card.
   *   `ghost` borderless 34px pills over a bottom rule — **page-level panel
   *           switching**, on 5 prototype pages (Source detail, Finding detail,
   *           Reconciliation sweep, Settings, Profile). A border here would draw a
   *           box around something that is really a heading row for the page.
   *   `queue` the review-queue selector: a 36px pill with a leading status dot and
   *           a trailing mono count, where the weight shifts with selection.
   *
   * Counts are baked into the label string in the prototype (`'Missing · 2,340'`);
   * `count` renders one properly instead, so it is tabular and announced once.
   */
  variant?: 'pill' | 'ghost' | 'queue';
  /**
   * The id of the `tabpanel` these tabs switch, set as `aria-controls` on every
   * tab. `tablist` only.
   *
   * Without it the `tablist` role is a promise the DOM does not keep: a screen
   * reader announces "tab, 1 of 2" and offers no way to reach the panel it names,
   * because nothing links them. The role commits to `tabpanel`s existing (see
   * `as` above) and this is half of what makes that true — the caller supplies
   * the other half by putting `role="tabpanel"` and `aria-labelledby` on the
   * element with this id.
   *
   * A single panel whose *content* swaps is the normal case here, so every tab
   * points at the same id; that is valid ARIA and simpler than one panel per tab.
   */
  controls?: string;
  /**
   * Fixes the generated tab element ids as `<idPrefix>-<item.id>`, instead of
   * deriving them from an internal `useId()`.
   *
   * Needed when the caller must reference the *selected* tab's id — a `tabpanel`
   * has to point back at its tab with `aria-labelledby`, and it cannot guess a
   * value React generated inside this component.
   */
  idPrefix?: string;
}

/**
 * Pill tabs / segmented control (design-system catalogue, Tier 2).
 *
 * Added by the design-fidelity audit: the prototype uses this shape on nearly
 * every screen and the catalogue had `Tabs` as Planned, so screens either went
 * without or improvised.
 *
 * Keyboard model follows the role. As a `tablist`, Left/Right move selection and
 * the group is a single tab stop (APG's tab pattern). As a `group`, every pill is
 * an ordinary button in the tab order, because a filter is a set of independent
 * controls rather than one control with several positions.
 */
export function Tabs({
  items,
  value,
  onChange,
  label,
  as = 'tablist',
  variant = 'pill',
  controls,
  idPrefix,
}: TabsProps) {
  const generatedId = useId();
  const groupId = idPrefix ?? generatedId;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (as !== 'tablist') return; // a `group` uses plain Tab navigation
    const dir = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    event.preventDefault();
    const index = items.findIndex((i) => i.id === value);
    // Wraps at both ends, per APG. Stopping at the end leaves a user pressing a
    // key that appears to do nothing.
    const next = items[(index + dir + items.length) % items.length];
    onChange(next.id);
    refs.current[next.id]?.focus();
  };

  return (
    <div className={`dpr-tabs dpr-tabs--${variant}`} role={as} aria-label={label}>
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(el) => {
              refs.current[item.id] = el;
            }}
            type="button"
            className="dpr-tabs__tab"
            role={as === 'tablist' ? 'tab' : undefined}
            id={as === 'tablist' ? `${groupId}-${item.id}` : undefined}
            aria-controls={as === 'tablist' ? controls : undefined}
            // aria-selected is the tab role's state; a `group` of filter buttons
            // uses aria-pressed, which is the toggle-button state. The CSS keys
            // off aria-selected, so a group also sets it — harmless on a button,
            // and it keeps one selector rather than two.
            aria-selected={selected}
            aria-pressed={as === 'group' ? selected : undefined}
            // Roving tabindex: one stop for the whole tablist.
            tabIndex={as === 'tablist' ? (selected ? 0 : -1) : undefined}
            aria-label={item.accessibleLabel}
            onClick={() => onChange(item.id)}
            onKeyDown={onKeyDown}
          >
            {item.dot ? (
              <span
                className={`dpr-tabs__dot dpr-tabs__dot--${item.dot}`}
                aria-hidden="true"
              />
            ) : null}
            {item.label}
            {item.count != null ? (
              // aria-hidden: the count is repeated in the accessible name below,
              // so announcing the bare number after the label would read as
              // "Duplicates 12 12".
              <span className="dpr-tabs__count" aria-hidden="true">
                {item.count}
              </span>
            ) : null}
            {item.count != null && !item.accessibleLabel ? (
              <VisuallyHidden>{`, ${item.count}`}</VisuallyHidden>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
